import 'whatwg-fetch';
import qs from 'qs';
import api from 'qa/api';
import { csrfFromDom } from 'common/utils/server';
import { notifySuccess, notifyError } from 'common/state/notificationState';

const FETCH_FORMS_REQUEST = 'formsPage/FETCH_FORMS_REQUEST';
const FETCH_FORMS_SUCCESS = 'formsPage/FETCH_FORMS_SUCCESS';
const EDIT_FORM = 'formsPage/EDIT_FORM';
const REMOVE_FORM = 'formsPage/REMOVE_FORM';
const RESTORE_FORM = 'formsPage/RESTORE_FORM';
const TOGGLE_ARCHIVED = 'formsPage/TOGGLE_ARCHIVED';
const TABLE_LOADING = 'formsPage/TABLE_LOADING';
const SET_PAGE = 'formsPage/SET_PAGE';
const SET_TYPE = 'formsPage/SET_TYPE';

// Selectors

export const getForms = state => state.qaForms.forms;
export const getLoading = state => state.qaForms.loading;
export const getArchived = state => state.qaForms.archived;
export const getPage = state => state.qaForms.page;
export const getTotalPages = state => state.qaForms.totalPages;
export const getType = state => state.qaForms.type;

// Action Creators

const fetchRequest = () => ({
  type: FETCH_FORMS_REQUEST,
});

const fetchFormsSuccess = ({ qa_forms, meta }) => ({
  type: FETCH_FORMS_SUCCESS,
  forms: qa_forms,
  totalPages: meta.total_pages,
});

export const tableLoading = () => ({
  type: TABLE_LOADING,
});

export const editForm = formId => ({
  type: EDIT_FORM,
  formId,
});

export const removeQaForm = formId => ({
  type: REMOVE_FORM,
  formId,
});

export const restoreQaForm = form => ({
  type: RESTORE_FORM,
  form,
});

export const toggleArchived = () => ({
  type: TOGGLE_ARCHIVED,
});

export const setPage = page => ({
  type: SET_PAGE,
  page,
});

export const setType = scorecardType => ({
  type: SET_TYPE,
  scorecardType,
});

// Thunks

export const fetchQAForms = options => (dispatch, getState) => {
  const {
    orderBy = 'created_at',
    orderDirection = 'desc',
    per = 10,
  } = options || {};
  const archived = getArchived(getState());
  const page = getPage(getState());
  const type = getType(getState());
  const params = qs.stringify({
    order_by: orderBy,
    order_direction: orderDirection,
    include_reviews_count: true,
    type,
    archived,
    per,
    page,
  });
  const url = `/api/v1/qa_forms?${params}`;

  dispatch(fetchRequest());

  return fetch(url)
    .then(result => result.json())
    .then(result => {
      dispatch(fetchFormsSuccess(result || {}));

      return result;
    });
};

export const duplicateQAForm = formId => dispatch => {
  const url = `/api/v1/qa_forms/${formId}/duplicate`;

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-token': csrfFromDom(),
    },
  })
    .then(result => {
      if (!result.ok) {
        throw Error(result.statusText);
      }
      return result.json();
    })
    .then(json => {
      window.location.assign(`/qa/forms/${json.id}`);
    })
    .catch(e => dispatch(notifyError(e.message)));
};

export const archiveQAForm =
  (formId, archiveReviews = false) =>
  dispatch => {
    const url = `qa_forms/${formId}/archive?archive_reviews=${archiveReviews}`;

    return api
      .fetch(dispatch, url, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRF-token': csrfFromDom() },
      })
      .then(() => {
        dispatch(removeQaForm(formId));
        dispatch(notifySuccess('Scorecard archived successfully.'));
      });
  };

export const unarchiveQAForm = formId => dispatch => {
  const url = `/qa_forms/${formId}/unarchive`;

  return api
    .fetch(dispatch, url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-token': csrfFromDom() },
    })
    .then(([form]) => {
      dispatch(restoreQaForm(form));
      dispatch(notifySuccess('Scorecard restored successfully.'));
    });
};

// Reducers

const initialState = {
  forms: [],
  totalPages: 0,
  page: 1,
  archived: false,
  editingFormId: null,
  loading: false,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_FORMS_REQUEST: {
      return { ...state, loading: true };
    }

    case TABLE_LOADING: {
      return { ...state, loading: true };
    }

    case TOGGLE_ARCHIVED: {
      return {
        ...state,
        archived: !state.archived,
      };
    }

    case FETCH_FORMS_SUCCESS: {
      return {
        ...state,
        forms: action.forms,
        totalPages: action.totalPages,
        loading: false,
      };
    }

    case REMOVE_FORM: {
      return {
        ...state,
        forms: state.forms.filter(form => form.id != action.formId),
      };
    }

    case RESTORE_FORM: {
      return {
        ...state,
        forms: state.forms.map(form => {
          return form.id === action.form.id ? action.form : form;
        }),
      };
    }

    case SET_PAGE: {
      return {
        ...state,
        page: action.page,
      };
    }

    case SET_TYPE: {
      return {
        ...state,
        type: action.scorecardType,
      };
    }

    case EDIT_FORM: {
      return {
        ...state,
        editingFormId: action.formId,
      };
    }

    default: {
      return state;
    }
  }
};
